/**
 * Approved advance-without-PI requests (pi_pending / overdue) shown inside the
 * Payment Receipts tab — flagged as "no vendor PI attached". Lets finance attach
 * payment slip(s) (record-only). Separate from the normal AdvancePaymentsTable so
 * the existing payments-pending flow is untouched.
 */

import React, { useRef, useState } from 'react';
import { Loader2, Upload, Paperclip, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { ProcurementApiService, DeviatedAdvanceRequest } from '../../services/procurementApi';

function formatAmount(v: number | null | undefined): string {
	if (v == null) return '—';
	return `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
}
function formatDate(v: string | null | undefined): string {
	if (!v) return '—';
	try {
		const d = new Date(v);
		return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
	} catch {
		return v;
	}
}

interface Props {
	rows: DeviatedAdvanceRequest[];
	loading: boolean;
	serialOffset: number;
	onSlipUploaded: () => void;
}

const DeviatedAdvanceSlipsTable: React.FC<Props> = ({ rows, loading, serialOffset, onSlipUploaded }) => {
	const [slipBusy, setSlipBusy] = useState<number | null>(null);
	const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
	const toastRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const fileRef = useRef<HTMLInputElement>(null);
	const targetRef = useRef<number | null>(null);

	const showToast = (type: 'success' | 'error', msg: string) => {
		setToast({ type, msg });
		if (toastRef.current) clearTimeout(toastRef.current);
		toastRef.current = setTimeout(() => setToast(null), 4000);
	};
	const pick = (id: number) => {
		targetRef.current = id;
		fileRef.current?.click();
	};
	const handleFile = async (file: File | null) => {
		const id = targetRef.current;
		if (!file || id == null) return;
		setSlipBusy(id);
		try {
			await ProcurementApiService.attachDeviatedAdvanceSlip(id, file);
			showToast('success', 'Payment slip attached.');
			onSlipUploaded();
		} catch (e: any) {
			showToast('error', e?.response?.data?.message || e?.message || 'Failed to attach slip.');
		} finally {
			setSlipBusy(null);
			if (fileRef.current) fileRef.current.value = '';
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400">
				<Loader2 className="h-4 w-4 animate-spin" /> Loading…
			</div>
		);
	}
	if (rows.length === 0) {
		return <div className="py-8 text-center text-sm text-gray-400">No approved advances without PI.</div>;
	}

	return (
		<div className="overflow-x-auto">
			{toast && (
				<div
					className={cn(
						'fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium',
						toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
					)}
				>
					{toast.msg}
				</div>
			)}
			<input
				ref={fileRef}
				type="file"
				accept=".pdf,.docx,.jpg,.jpeg,.png,.webp"
				className="hidden"
				onChange={e => handleFile(e.target.files?.[0] ?? null)}
			/>
			<table className="w-full text-sm">
				<thead>
					<tr className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
						<th className="px-3 py-2">#</th>
						<th className="px-3 py-2">Client</th>
						<th className="px-3 py-2">Vendor</th>
						<th className="px-3 py-2 text-right">Approved</th>
						<th className="px-3 py-2">PI deadline</th>
						<th className="px-3 py-2">Slips</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((r, idx) => {
						const overdue = r.status === 'overdue' || r.isOverdue;
						return (
							<tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
								<td className="px-3 py-2.5 text-gray-500">{serialOffset + idx + 1}</td>
								<td className="px-3 py-2.5">
									<div className="font-medium text-gray-900">{r.leadClient ?? '—'}</div>
									<div className="text-[11px] text-gray-400">Lead #{r.leadId}</div>
								</td>
								<td className="px-3 py-2.5">
									<div className="text-gray-700">{r.vendorName}</div>
									<span className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
										<AlertTriangle className="h-2.5 w-2.5" />
										No vendor PI attached
									</span>
								</td>
								<td className="px-3 py-2.5 text-right font-semibold text-green-700 tabular-nums">
									{formatAmount(r.amountApproved)}
								</td>
								<td className="px-3 py-2.5 text-xs">
									{r.piDeadlineAt ? (
										<span className={overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}>
											{formatDate(r.piDeadlineAt)}
											{overdue ? ' · overdue' : r.daysRemaining != null ? ` · ${r.daysRemaining}d left` : ''}
										</span>
									) : (
										<span className="text-gray-300">—</span>
									)}
								</td>
								<td className="px-3 py-2.5">
									<div className="flex items-center gap-2 flex-wrap">
										{r.paymentSlipUrls.map((url, i) => (
											<a
												key={url}
												href={url}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
											>
												<Paperclip className="h-3 w-3" />
												Slip {r.paymentSlipUrls.length > 1 ? i + 1 : ''}
											</a>
										))}
										<button
											onClick={() => pick(r.id)}
											disabled={slipBusy === r.id}
											className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
										>
											{slipBusy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
											Attach slip
										</button>
									</div>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

export default DeviatedAdvanceSlipsTable;
